/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { Brand } from '@/lib/brand/brand';

/**
 * Which mount the rail is on. Mirrors the `surface` prop the two routes already
 * pass, rather than introducing a second vocabulary for the same distinction.
 */
export type FacetSurface = 'plp' | 'search';

/**
 * WHAT ORDER THE FACET RAIL'S GROUPS GO IN (pattern: keyed map, never an inline
 * brand branch — see QUICK_FILTERS_VARIANT_BY_BRAND / PLP_UX_BY_BRAND).
 *
 * THE RAIL USED TO RENDER SCAPI'S RESPONSE ORDER VERBATIM, and that order is
 * not live's. Measured 2026-08-22 by decoding the routes' own loader payloads
 * on the running app — eight of them, both brands, both surfaces (NM
 * womens-clothing, NM dresses, NM search; BG womens-clothing, BG shoes, BG
 * handbags, BG jewelry, BG search). Every one returns the same five groups in
 * the same order:
 *
 *     cgid | brand | price | c_size | c_color
 *
 * so the rail painted Category, Designer, PRICE, Size, Color on all four
 * brand-by-surface combinations. Live puts Price LAST of the five on three of
 * those four, and second-to-last on the fourth. The divergence was therefore
 * never a component bug and never a per-category one: it is one wrong constant,
 * applied uniformly, and it is ours to fix because the response order is
 * something we consume rather than something we are bound by.
 *
 * WHAT LIVE ACTUALLY ORDERS. Counted with a real DOM engine (jsdom) over live's
 * own saved markup, each list bounded by its CONTAINER and its children
 * enumerated in order — never by a match count, which is what made an earlier
 * reading of this rail wrong. BG's container is the single `div.rc-collapse`
 * with `div.rc-collapse-item` children; NM's is `nav#faceted-left-nav` with
 * `div.faceted-left-nav__filter` children, plus the separate Category control
 * that NM renders through `button.search-results__cat-button` and that a
 * filter-class enumeration alone cannot see. Every file carries one `html` and
 * one `body`, so unlike the decompiled captures these are emitted ONCE and the
 * counts stand as read. Control on each: 203 to 810 `a[href]`.
 *
 *   NM PLP      Category, Get it Fast, Designer, Size, COLOR, PRICE, Sale,
 *               Type, Occasion, Availability — 10 groups
 *               (dumps/nm-plp-d-1280, canonical neimanmarcus.com
 *               /c/womens-clothing-cat58290731)
 *   NM search   Category, Get it Fast, Gender, Designer, Size, COLOR, PRICE,
 *               Availability, Sale — 9 groups
 *               (dumps/nm-search-d-1280, title "Dress at Neiman Marcus")
 *   BG search   Category, Designer, Size, COLOR, PRICE, Availability, Sale,
 *               Gender — 8 groups, and identical in two independent files
 *               (dumps/bg-search-d-1280 and bg-search-dress.desktop.html)
 *   BG PLP      Category, Designer, Size, PRICE, COLOR, then whatever else the
 *               category carries — PRICE AND COLOR ARE TRANSPOSED against
 *               every other surface, and this is the whole reason this map is
 *               keyed by surface as well as brand.
 *
 * BG'S PLP EXCEPTION IS NOT A ONE-CAPTURE ARTEFACT, which is the failure mode
 * this repo has already paid for once on this exact rail. It reproduces across
 * TWO DIFFERENT CATEGORIES and two widths:
 *   womens-clothing-cat441206 — six groups, Price 4th, Color 5th
 *     (dumps/bg-plp-d-1280 at 1280, dumps/bg-plp-m-440 at 440, and the older
 *     duplicate save bg-plp-d-1280-dup-older, all agreeing)
 *   womens-clothing-clothing-dresses-cat80001 — twelve groups, Price 4th,
 *     Color 5th (bg-plp-womens-dresses.desktop.html)
 * BG's facet SET is server-driven per category — those two categories share
 * only five of their groups — but the ORDER of the groups they share is stable,
 * and it is stably different from BG's own search rail. So the surface is the
 * right key and the category is not.
 *
 * UNRANKED FACETS KEEP THEIR RESPONSE ORDER AND LAND AFTER THE RANKED ONES.
 * The sandbox returns nothing outside these five today, so this arm is
 * forward-looking, and it is the arm that matches live wherever live has extra
 * groups to place: BG hangs Sustainability Type, pattern, Dress Length, Style,
 * Sleeve Length and Material after Color, and NM hangs Sale, Type and Occasion
 * after Price. The one live group it would place wrongly is NM's Get it Fast,
 * which sits SECOND on both NM surfaces. Rank it here if it ever arrives in a
 * payload — do not infer a general rule from it, because it is the only group
 * on either brand that leads.
 *
 * `cgid` is listed for completeness only. The rail renders it through its own
 * leading block when `categoryFacetFirst` is set and drops it otherwise, so it
 * never reaches the sort.
 *
 * GET IT FAST IS NOT IN THIS MAP AND CANNOT BE — see `INJECTED_GROUP_SLOT`
 * below. It is not a SCAPI refinement, so it never reaches this sort at all.
 */
const FACET_GROUP_ORDER: Record<Brand, Record<FacetSurface, readonly string[]>> = {
    nm: {
        /*
         * NM's PLP is deliberately WITHOUT `c_refGender`, and the asymmetry with
         * `search` below is the honest reading rather than an oversight.
         *
         * Live NM's PLP does not render a Gender group at all -- neither PLP dump
         * carries it, against 9 filterKeys on NM's and 13 on BG's. Ours does,
         * because the refinement is defined on `root` and therefore inherits to
         * every category. Whether live suppresses it as policy or because both
         * captured PLPs are all-women categories (a single-value group) is
         * genuinely undecidable from two captures, so the group's ORDER here is
         * unmeasured and it is left unranked -- it sorts to the end of the SCAPI
         * run, which is where an unranked group has always gone. Ranking it would
         * be inventing a position for a group live may not draw at all.
         */
        plp: ['cgid', 'brand', 'c_size', 'c_color', 'price'],
        /*
         * `c_refGender` sits between Category and Designer because that is where
         * live NM puts it. Read off `dumps/nm-search-d-1280` by bounding
         * `#faceted-left-nav`, live's rendered order is:
         *
         *     Category, Get it Fast, Gender, Designer, Size, Color, Price,
         *     Availability, Sale
         *
         * Get it Fast is the injected group and takes its position from
         * INJECTED_GROUP_SLOT below rather than from this list, so ranking Gender
         * second here lands it third on the page, which is live's slot.
         *
         * BG IS DELIBERATELY UNCHANGED. Live BG carries Gender LAST of its eight
         * groups, and ours already lands it there by being unranked -- so adding
         * it to BG's arms would move a group that currently matches live. A rank
         * is only worth adding where the unranked position is wrong.
         */
        search: ['cgid', 'c_refGender', 'brand', 'c_size', 'c_color', 'price'],
    },
    bg: {
        plp: ['cgid', 'brand', 'c_size', 'price', 'c_color'],
        search: ['cgid', 'brand', 'c_size', 'c_color', 'price'],
    },
};

/**
 * Where the rail's ONE INJECTED group — `RefineInventory`, headed "Get it Fast"
 * — sits relative to the groups SCAPI sends.
 *
 * `'after-category'`  immediately after the leading Category block
 * `'last'`            after every SCAPI group, which is where it used to sit
 *                     unconditionally
 * `'none'`            the group is not rendered on this brand at all
 *
 * THE SLOT IS A RELATION, NOT AN INDEX, and that is the whole point. The rail's
 * membership is server-driven and moves under us — a Gender refinement is being
 * authored into the catalogue as this lands, and BG's facet set is per-category
 * — so any rule of the form "position 2" or "second of nine" is wrong the
 * moment the payload changes. Both slots are expressed as a position between
 * two RENDER BLOCKS in `index.tsx`, so a facet arriving or disappearing lands
 * inside the block it belongs to and cannot displace this group.
 *
 * WHY NM IS `'after-category'`. Live NM puts Get it Fast immediately after
 * Category on BOTH of its listing surfaces. Re-derived 2026-08-23 with a real
 * DOM engine (jsdom, scripts not executed) over live's own saved markup, each
 * list bounded by its container — `nav#faceted-left-nav`, descending to
 * `div.faceted-left-nav__child__mobile`, whose children are the
 * `div.faceted-left-nav__filter` groups plus the separate
 * `div.faceted-left-nav__filter__title` that carries NM's Category control:
 *
 *   NM search   Category (title block), then filters in document order:
 *               In Store, Gender, Designer, Size, Color, Price, Availability,
 *               Sale — 8 filters, 9 groups. Get it Fast is the FIRST filter.
 *               (dumps/nm-search-d-1280, control 618 `a[href]`)
 *   NM PLP      Category (as a filter, no separate title block here), then
 *               In Store, Designer, Size, Color, priceBandLower, Sale, level2,
 *               occasionStyle, Availability — 10 filters. Get it Fast is again
 *               the group immediately after Category.
 *               (dumps/nm-plp-d-1280, control 810 `a[href]`)
 *
 * WHEN THERE IS NO CATEGORY BLOCK, `'after-category'` degrades to FIRST, which
 * is the honest reading of a relative slot whose anchor is absent. No real
 * mount reaches that state — both routes pass `categoryFacetFirst`, the search
 * one through `SearchUx`, which sets it for both brands — so it is a story and
 * fixture condition only. It is written down because the storybook snapshot
 * shows it and a reader would otherwise take it for a defect.
 *
 * `filter-key-In Store` is live's own handle for the group; the values under it
 * are csp / sdd / ndd, of which we render only the store-pickup arm. See the
 * docstring on `RefineInventory` for why this group is Get it Fast and not
 * live's Availability, which is a different facet we render from `c_preOrder`.
 *
 * WHY BG IS `'none'`. Live BG has no store-pickup facet under any name, on any
 * surface, at any width anyone has captured — so the group is not misplaced on
 * this brand, it is `[ours-extra]`, and the honest slot for a group live does
 * not draw is no slot at all.
 *
 * THREE INDEPENDENT LIVE SOURCES, none derived from another, each bounded by its
 * own container and enumerated rather than string-counted:
 *
 *   BG search, desktop   `div.rc-collapse` with `div.rc-collapse-item` children:
 *                        Category, Designer, Size, Color, Price, Availability,
 *                        Sale, Gender — 8 groups, control 496 `a[href]`
 *                        (dumps/bg-search-d-1280, jsdom).
 *   BG PLP, mobile       live's own filter takeover at 390 CSS / DPR 2, opened
 *                        through the bottom `Filters` pill on
 *                        bergdorfgoodman.com/c/womens-clothing-cat441206 and
 *                        measured in the page: `ul.more-filters-content` with
 *                        six `li` children — Category, Designer, Size, Price,
 *                        Color, Sustainability Type.
 *                        (.reference/live-bg-filter-drilldown/, 2026-08-23)
 *   BG search, mobile    the decompiled 375 capture
 *                        captures/bg-search-m-filters-375: the same `ul`, seven
 *                        `li` — Category, Designer, Size, Color, Price,
 *                        Availability, Sale. Live's own `filterOptions` query
 *                        parameter in that capture's recorded URL names the same
 *                        seven and no others.
 *
 * Zero store-pickup group in any of them, and the three disagree about the SET
 * (6 / 7 / 8) exactly as a server-driven per-category facet list should — which
 * is what makes the shared absence evidence rather than one capture's accident.
 *
 * WHAT THIS DOES NOT CLAIM. It is not a judgement on whether store pickup is a
 * feature BG should have; it is a judgement about what BG's rail renders today.
 * A BG capture showing a store-pickup facet moves this entry back to `'last'`,
 * or to whatever slot that capture shows, and nothing else has to change.
 *
 * NM IS UNTOUCHED and must stay so: live NM draws this group SECOND on both of
 * its listing surfaces, and ours already matches. See the NM paragraph above.
 *
 * The brand keys are reached LITERALLY for the same reason `orderFor` below
 * does it: `src/__tests__/declared-never-read` resolves `x.field` but not
 * `x[key]`, so a map read through a variable key reads as dead while it paints.
 */
export type InjectedGroupSlot = 'after-category' | 'last' | 'none';

const INJECTED_GROUP_SLOT: Record<Brand, InjectedGroupSlot> = {
    nm: 'after-category',
    bg: 'none',
};

/** Slot for the rail's injected Get it Fast group. See `INJECTED_GROUP_SLOT`. */
export function injectedGroupSlot(brand: Brand): InjectedGroupSlot {
    return brand === 'bg' ? INJECTED_GROUP_SLOT.bg : INJECTED_GROUP_SLOT.nm;
}

/**
 * DOES OPENING A GROUP CLOSE THE ONE THAT WAS OPEN?
 *
 * BG: yes. Live BG's rail is an EXCLUSIVE accordion — at most one group is
 * expanded at any moment. Two independent sources, neither derived from the
 * other:
 *
 *   the owner, 2026-08-23, on live BG search at 1728 — "live site enforces only
 *   one left nav top level option open at once, if you expand designers it
 *   closes categories."
 *
 *   his six captures of that same page, one per group, taken minutes apart:
 *   owner-live-bg-facet-{size,color,price,availability,gender,sale}-1728.png.
 *   Counting group frames off the rail's own 1px dividers, EVERY one of the six
 *   shows exactly ONE section taller than the flat 53px header row and the other
 *   seven at exactly 53px. Opening COLOR had closed SIZE; opening SALE had
 *   closed everything else. Six independent draws, six times one-open — which is
 *   what an exclusive accordion produces and is vanishingly unlikely from a
 *   multi-open rail a person is clicking through.
 *
 * NM: NOT ESTABLISHED, so it keeps the multi-open behaviour it already had.
 * Live NM's rail is a different component family (`faceted-left-nav`, not BG's
 * `rc-collapse`) and no NM capture on disk shows two groups in a state that
 * could settle it either way. `false` here is "unmeasured, unchanged", not a
 * measurement — flipping it needs an NM capture, not a symmetry argument.
 *
 * WHAT THIS DOES NOT REACH, and it is deliberate: the injected Get it Fast group
 * is drawn by extensions/bopis/components/refine-inventory, which owns its own
 * open state and is not a `FilterSection`, so exclusivity cannot close it.
 *
 * THAT ASYMMETRY IS NOW UNREACHABLE ON BG rather than merely harmless, because
 * `INJECTED_GROUP_SLOT.bg` is `'none'` and the group does not render on this
 * brand at all — and BG is the only brand that asks for exclusivity. It is
 * written down rather than deleted because the two facts are independent: if a
 * BG capture ever restores that group to the rail, the asymmetry comes back with
 * it and is the thing to look at.
 *
 * The brand keys are reached LITERALLY, for the reason `injectedGroupSlot` above
 * records: `src/__tests__/declared-never-read` resolves `x.field` but not
 * `x[key]`, so a map read through a variable key reads as dead while it paints.
 */
const EXCLUSIVE_ACCORDION: Record<Brand, boolean> = {
    nm: false,
    bg: true,
};

/**
 * Does this brand's facet rail allow only ONE open group at a time?
 * See `EXCLUSIVE_ACCORDION` for the evidence and for what it deliberately
 * leaves alone.
 */
export function exclusiveAccordion(brand: Brand): boolean {
    return brand === 'bg' ? EXCLUSIVE_ACCORDION.bg : EXCLUSIVE_ACCORDION.nm;
}

/**
 * DOES THIS BRAND RENDER THE COLOUR GROUP AS LIVE'S VALUE ROWS?
 *
 * NM — yes, and the shape is measured. Live NM at 1728 CSS with three filters
 * applied paints each colour value as ONE ROW: a filled disc, the label beside
 * it, the selected row's label bold, and NO hit count anywhere in the group.
 * Two values, `Black` selected and `Pink` not, in the owner capture
 * `live-shots/owner-2026-08-23-filters/filters-5-49-14PM.png` — provenance read
 * from its own address bar, `neimanmarcus.com/c/nm-offers--sales-cat66120739`.
 *
 * BG — NO, and that is `unmeasured` rather than a decision. The only live BG
 * filter evidence on disk is `bg-filters-dt.html` in that same directory, and
 * its Color group is `aria-expanded="false"` — COLLAPSED, so the capture
 * contains the group header and not one value row. Nothing on disk states what
 * BG paints inside it. An unmeasured surface takes the existing renderer
 * unchanged rather than inheriting NM's shape.
 *
 * WHY THIS IS KEYED ON BRAND AND NOT ON THE `rail` VARIANT, which is the
 * obvious-looking alternative and is wrong: `routes/_app.search.tsx` passes
 * `variant="rail"` to the BG MOBILE overlay too, deliberately and with its own
 * comment, because live's mobile panel is a flat list on both brands. Gating on
 * the variant would therefore have moved BG at mobile — the one place BG and NM
 * share a variant — on evidence that covers neither.
 *
 * The brand keys are reached LITERALLY for the reason `injectedGroupSlot`
 * records: the declared-never-read check resolves a dotted field and not an
 * indexed one, so a map read through a variable key reads as dead while it
 * paints.
 */
const COLOR_VALUE_ROWS: Record<Brand, boolean> = {
    nm: true,
    bg: false,
};

/**
 * Does this brand paint the colour group as live's one-value-per-row list?
 * See `COLOR_VALUE_ROWS` for the evidence and for what it leaves alone.
 */
export function colorValueRows(brand: Brand): boolean {
    return brand === 'bg' ? COLOR_VALUE_ROWS.bg : COLOR_VALUE_ROWS.nm;
}

/**
 * THE COLOUR GROUP ARRIVES UNDER TWO DIFFERENT IDS IN THIS REPO, and both are
 * real, so the rank has to recognise both or the group lands in live's slot on
 * only one of them.
 *
 *   `c_color`             what the sandbox actually returns — all eight loader
 *                         payloads decoded 2026-08-22, both brands, both
 *                         surfaces, without exception
 *   `c_refinementColor`   what this component's own value-renderer switch keys
 *                         on, and what the mock fixtures under
 *                         `components/__mocks__` carry
 *
 * Mapping the alias onto the canonical id is the narrow fix for the ORDER
 * question and nothing else. It does NOT reconcile the two spellings for the
 * renderer — under the ids the sandbox really sends, the colour group falls
 * through that switch to the default renderer rather than the swatch one. That
 * is a separate defect on a separate axis and is deliberately left alone here.
 */
const ATTRIBUTE_ALIASES: Readonly<Record<string, string>> = { c_refinementColor: 'c_color' };

/**
 * Rank of a facet group for the given brand and surface. Unranked groups sort
 * after every ranked one, and `Number.MAX_SAFE_INTEGER` rather than `Infinity`
 * keeps the comparator's subtraction finite when two unranked groups meet.
 */
export function facetGroupRank(brand: Brand, surface: FacetSurface, attributeId: string): number {
    const canonical = ATTRIBUTE_ALIASES[attributeId] ?? attributeId;
    const index = orderFor(brand, surface).indexOf(canonical);
    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
}

/**
 * The map's two surface arms, reached by LITERAL keys on purpose.
 *
 * `FACET_GROUP_ORDER[brand][surface]` would be shorter and would read as an
 * unread field to `src/__tests__/declared-never-read`, which resolves
 * `x.field` and `x['field']` but not `x[key]` — so a brand switch reached
 * through a variable key looks dead to the detector while it paints. The
 * ternary is the honest fix rather than an entry in that test's debt list:
 * both arms genuinely are read, and now the check can see it.
 */
function orderFor(brand: Brand, surface: FacetSurface): readonly string[] {
    return surface === 'search' ? FACET_GROUP_ORDER[brand].search : FACET_GROUP_ORDER[brand].plp;
}

/**
 * Order a rail's groups for one brand and surface.
 *
 * The sort is STABLE by construction: ties — which is every pair of unranked
 * groups — compare equal, and `Array.prototype.sort` has been required to be
 * stable since ES2019, so unranked groups come out in the order the response
 * gave them. That is deliberate. An unranked group is one we have no live
 * evidence for, and response order is the only ordering we can honestly claim
 * for it.
 */
export function orderFacetGroups<T extends { attributeId?: string }>(
    groups: readonly T[],
    brand: Brand,
    surface: FacetSurface
): T[] {
    return [...groups].sort(
        (a, b) =>
            facetGroupRank(brand, surface, a.attributeId ?? '') - facetGroupRank(brand, surface, b.attributeId ?? '')
    );
}
